import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Flex,
  Heading,
  useBreakpointValue,
} from "@chakra-ui/react";
import { format as formatDate, parseISO as parseISODate } from "date-fns";
import { translate as trans } from "../Trans/Trans";
import DatePicker from "../DatePicker/DatePicker";
import TimePicker from "../TimePicker/TimePicker";

const DateTimePicker = ({ dateTime, showClearButton, onChange }) => {
  const [state, setState] = useState();
  const isSmallScreen = useBreakpointValue({ base: true, md: false });
  const dateObject = useMemo(
    () => (state ? parseISODate(state) : undefined),
    [state]
  );

  const handleDayChange = useCallback(
    (newDateTime) => {
      setState(formatDate(newDateTime, "yyyy-MM-dd HH:mm"));
    },
    [setState]
  );

  const handleTimeChange = useCallback(
    (newDateTime) => {
      setState(formatDate(newDateTime, "yyyy-MM-dd HH:mm"));
    },
    [setState]
  );

  const handleClearClick = useCallback(() => {
    setState(null);
  }, [setState]);

  useEffect(() => {
    if (state === undefined) {
      return;
    }

    if (state !== dateTime) {
      onChange?.(state);
    }
  }, [state]);

  useEffect(() => {
    setState(dateTime);
  }, [dateTime]);

  useEffect(() => {
    setState(dateTime);
  }, []);

  return (
    <Flex direction={isSmallScreen ? "column" : "row"}>
      <Box sx={{ fontSize: "xs" }}>
        <DatePicker
          month={dateObject}
          selectedDays={dateObject}
          onDayClick={handleDayChange}
        />
      </Box>
      <Flex
        direction="column"
        align="stretch"
        justify="center"
        paddingTop={isSmallScreen ? "2" : null}
        paddingLeft={isSmallScreen ? null : "2"}
      >
        <Box textAlign="center">
          <Heading fontSize="sm" marginBottom="2">
            {trans("date_time_picker.time")}
          </Heading>
          <TimePicker size="sm" date={dateObject} onChange={handleTimeChange} />
        </Box>
        {showClearButton ? (
          <Box marginTop="4" textAlign="center">
            <Button
              size="sm"
              onClick={handleClearClick}
              isDisabled={!dateObject}
            >
              {trans("date_time_picker.clear")}
            </Button>
          </Box>
        ) : null}
      </Flex>
    </Flex>
  );
};

DateTimePicker.propTypes = {
  dateTime: PropTypes.string,
  showClearButton: PropTypes.bool,
  onChange: PropTypes.func,
};

DateTimePicker.defaultProps = {
  dateTime: undefined,
  showClearButton: undefined,
  onChange: undefined,
};

export default memo(DateTimePicker);
